import { FC, PropsWithChildren, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ErrorBoundary,
  FallbackProps,
  useErrorBoundary,
} from "react-error-boundary";
import Loading from "../../../components/Loading";
import { getSSOImplementation } from "../auth";
import {
  initializeAuthSpaSSO as initializeSpaSSO,
  useIsInitializedSpaSSO as useIsInitializedSpaSSO,
} from "./store";
import {
  initializeExternalSSO,
  useIsInitializedExternalSSO,
} from "../external";
import { Fallback as FallbackNoIntl } from "../../../components/ErrorFallback";
import { useSSOLocale } from "../../../components/login/customHooks";
import translations from "../../../../i18n";
import { IntlProvider } from "react-intl";

const AuthGuardInner: FC<PropsWithChildren> = ({ children }) => {
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const ssoImplementation = getSSOImplementation();
  const isInitializedSpaSSO = useIsInitializedSpaSSO();
  const isInitializedExternal = useIsInitializedExternalSSO();

  const initialized =
    ssoImplementation === "spaSSO"
      ? isInitializedSpaSSO
      : isInitializedExternal;

  useEffect(() => {
    const initialize = async () => {
      if (initialized) return;
      try {
        switch (ssoImplementation) {
          case "spaSSO": {
            await initializeSpaSSO(navigate);
            break;
          }
          case "externalSSO": {
            initializeExternalSSO();
            break;
          }
        }
      } catch (error) {
        showBoundary(
          error instanceof Error
            ? error
            : new Error("Unknown error while initializing auth"),
        );
      }
    };

    void initialize();
  }, [ssoImplementation, navigate, showBoundary, initialized]);

  return <>{initialized ? <>{children}</> : <Loading />}</>;
};

const TranslatedFallback: FC<FallbackProps> = (props) => {
  const ssoLocale = useSSOLocale();
  return (
    <IntlProvider
      locale={ssoLocale}
      messages={translations[ssoLocale]}
      defaultLocale="en"
    >
      <FallbackNoIntl {...props} />
    </IntlProvider>
  );
};

export const AuthGuard: FC<PropsWithChildren> = ({ children }) => {
  return (
    <ErrorBoundary
      // Since this is outside of TRPC, we need this fallback locale mechanism.
      FallbackComponent={TranslatedFallback}
      // resetKeys={[ssoImplementation]}
    >
      <AuthGuardInner>{children}</AuthGuardInner>
    </ErrorBoundary>
  );
};
